/**
 * S03 · Mode Picker — radial dial. Port of the showcase dial, wired to
 * TanStack Router + the `captureSessionStore`.
 *
 * Every mode on the dial captures. It did not always: the dial shipped with
 * Note alone implemented and the other five rendered disabled to match the
 * mock, behind a toast that said they were coming. They arrived one at a time
 * and the toast was never removed, so a dead branch sat here promising work
 * that had already shipped. There is no disabled state left to model — a mode
 * that cannot capture has no business being on the dial.
 */
import type { ReactNode } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { CaptureKind } from '@kapture/types';
import { Chip, GestureBar, I, Ico, StatusBar, T } from '@/components/primitives';
import { useCaptureSessionStore, useSession } from '@kapture/client-core';

export const Route = createFileRoute('/sessions/$sessionId/modes')({
  component: ModePickerSheet,
  staticData: { mobile: true, modal: true, fullscreen: true },
});

/**
 * `signature` is not on the dial, and the type says so.
 *
 * Every other kind is something an inspector decides to capture; a signature
 * answers a specific form field and is started from that field. Narrowing
 * here rather than adding a dial button keeps the exhaustive switch in
 * `pick()` honest — it still fails to compile if a genuinely dial-able kind
 * is added and left unhandled.
 */
type DialKind = Exclude<CaptureKind, 'signature'>;

interface ModeDef {
  ang: number;
  icon: string | ReactNode;
  label: string;
  kind: DialKind;
}

/**
 * Six modes at 60°, previously five at 72°.
 *
 * The geometry needs no other change: the ring radius is 104, so the chord
 * between neighbours at 60° is 104 — still comfortably clear of the 72px
 * buttons. Photo stays at the top, and every other mode keeps its clockwise
 * order, so the only muscle memory this costs is the exact angle.
 */
const MODES: ModeDef[] = [
  { ang: -90, icon: I.camera, label: 'Photo', kind: 'photo' },
  { ang: -30, icon: I.video, label: 'Video', kind: 'video' },
  { ang: 30, icon: I.mic, label: 'Voice', kind: 'voice' },
  { ang: 90, icon: I.note, label: 'Note', kind: 'note' },
  { ang: 150, icon: I.pin, label: 'Location', kind: 'gps' },
  { ang: 210, icon: I.tag, label: 'Barcode', kind: 'barcode' },
];

function ModePickerSheet() {
  const navigate = useNavigate();
  const { sessionId } = Route.useParams();
  const { data: session } = useSession(sessionId);
  const setMode = useCaptureSessionStore((s) => s.setMode);
  const setActiveSession = useCaptureSessionStore((s) => s.setActiveSession);

  function close() {
    navigate({ to: '/sessions/$sessionId', params: { sessionId } });
  }

  function pick(mode: ModeDef) {
    setActiveSession(sessionId);
    setMode(mode.kind);
    switch (mode.kind) {
      case 'note':
        navigate({ to: '/sessions/$sessionId/capture/note', params: { sessionId } });
        return;
      case 'photo':
        navigate({ to: '/sessions/$sessionId/capture/photo', params: { sessionId } });
        return;
      case 'voice':
        navigate({ to: '/sessions/$sessionId/capture/voice', params: { sessionId } });
        return;
      case 'gps':
        navigate({ to: '/sessions/$sessionId/capture/gps', params: { sessionId } });
        return;
      case 'barcode':
        navigate({ to: '/sessions/$sessionId/capture/barcode', params: { sessionId } });
        return;
      case 'video':
        navigate({ to: '/sessions/$sessionId/capture/video', params: { sessionId } });
        return;
    }
    // Exhaustive over `CaptureKind`, and deliberately so. This used to end in
    // `default: return`, which meant a new kind added to the enum produced a
    // dial button that silently did nothing — no navigation, no toast, no
    // error. The assignment below fails to compile instead.
    const unreachable: never = mode.kind;
    return unreachable;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Pick capture mode"
      onClick={close}
      style={{
        position: 'absolute',
        inset: 0,
        background: T.hudBg,
        color: T.hudInk,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: T.font,
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ display: 'contents' }}>
        <StatusBar dark />
        <div
          style={{
            padding: '8px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close mode picker"
            style={{
              width: 32,
              height: 32,
              borderRadius: 16,
              background: 'rgba(253,246,238,0.09)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: T.hudInk,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <Ico d={I.back} size={16} />
          </button>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: 10,
                color: T.hudInkDim,
                fontFamily: T.mono,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              Capturing
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, fontFamily: T.font }}>
              {session?.title ?? 'Session'}
            </div>
          </div>
          <div style={{ width: 32 }} />
        </div>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <div style={{ position: 'relative', width: 280, height: 280 }}>
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%,-50%)',
                textAlign: 'center',
                color: T.hudInkDim,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontFamily: T.mono,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                Pick mode
              </div>
              <div style={{ fontSize: 11, fontFamily: T.font, marginTop: 2 }}>All modes live</div>
            </div>
            {MODES.map((m, i) => {
              const r = 104;
              const rad = (m.ang * Math.PI) / 180;
              const x = Math.cos(rad) * r;
              const y = Math.sin(rad) * r;
              // Quiet raised-dark dials; Harvest Flame stays reserved for the
              // moment a mode actually starts capturing.
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => pick(m)}
                  aria-label={m.label}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                    width: 72,
                    height: 72,
                    borderRadius: 36,
                    background: 'rgba(253,246,238,0.09)',
                    border: '1px solid rgba(253,246,238,0.24)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    color: T.hudInk,
                    cursor: 'pointer',
                    fontFamily: T.font,
                  }}
                >
                  <Ico d={m.icon} size={22} />
                  <div style={{ fontSize: 10, fontWeight: 600 }}>{m.label}</div>
                </button>
              );
            })}
            <div
              style={{
                position: 'absolute',
                inset: 40,
                borderRadius: '50%',
                border: '1px dashed rgba(253,246,238,0.16)',
                pointerEvents: 'none',
              }}
            />
          </div>
        </div>
        <div
          style={{
            padding: '12px 16px',
            display: 'flex',
            gap: 6,
            justifyContent: 'center',
          }}
        >
          {/* Only claims the session can back up. */}
          {session?.templateId && (
            <Chip tone="hud" size="sm" icon={<Ico d={I.file} size={10} />}>
              Form attached
            </Chip>
          )}
          <Chip tone="hud" size="sm">
            <span style={{ fontFamily: T.mono, letterSpacing: '0.06em' }}>
              REC ·{' '}
              {sessionId
                .replace(/[^a-z0-9]/gi, '')
                .slice(0, 8)
                .toUpperCase()}
            </span>
          </Chip>
        </div>
        <GestureBar dark />
      </div>
    </div>
  );
}
